import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RelatedResources from "@/components/RelatedResources";
import { trackSignUp } from "@/lib/tracking";
import {
  GLOSSARY_GROUPS,
  GLOSSARY_LETTERS,
  GLOSSARY_TERM_COUNT,
  type GlossaryLetterGroup,
  type GlossaryTerm,
} from "@/data/glossary";

const SIGN_UP_URL = "https://eu.bigdelta.com/sign-up";

const Hero = () => (
  <section className="relative pt-40 md:pt-52 pb-16 md:pb-24 px-6 md:px-10 bg-gradient-to-b from-muted/50 to-background border-b border-border/60">
    <div className="max-w-3xl mx-auto flex flex-col items-center text-center gap-7">
      <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
        Glossary · {GLOSSARY_TERM_COUNT} terms
      </span>
      <h1
        className="text-5xl md:text-7xl leading-[1.05] tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Web analytics
        <br />
        glossary.
      </h1>
      <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl">
        Every term you'll run into while reading about website analytics, product metrics, and
        session replay — defined in plain English, with links to deeper guides where we have them.
      </p>
    </div>
  </section>
);

/** Sticky A–Z jump bar; letters without entries render as inert placeholders. */
const LetterBar = () => (
  <nav
    aria-label="Skip to letter"
    className="sticky top-16 md:top-20 z-20 border-b border-border/60 bg-background/90 backdrop-blur px-6 md:px-10"
  >
    <ul className="max-w-5xl mx-auto flex flex-wrap justify-center gap-x-1 gap-y-1 py-3">
      {GLOSSARY_LETTERS.map(({ letter, hasTerms }) => (
        <li key={letter}>
          {hasTerms ? (
            <a
              href={`#letter-${letter.toLowerCase()}`}
              className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium text-foreground hover:bg-accent/15 hover:text-accent transition-colors"
            >
              {letter}
            </a>
          ) : (
            <span
              aria-hidden
              className="flex h-8 w-8 items-center justify-center rounded-full text-sm text-muted-foreground/40"
            >
              {letter}
            </span>
          )}
        </li>
      ))}
    </ul>
  </nav>
);

const TermRow = ({ term }: { term: GlossaryTerm }) => (
  <div id={term.id} className="scroll-mt-40 py-7 first:pt-0 md:py-8 md:first:pt-0">
    <h3 className="text-lg md:text-xl font-medium tracking-tight text-foreground">{term.term}</h3>
    <p className="mt-3 text-[15px] md:text-base text-muted-foreground leading-relaxed">
      {term.definition}
    </p>
    {term.link && (
      <Link
        to={term.link.to}
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-foreground hover:text-accent transition-colors"
      >
        Read more: {term.link.label}
        <ArrowUpRight className="w-4 h-4" />
      </Link>
    )}
    {term.productLink && (
      <Link
        to={term.productLink.to}
        className="mt-3 ml-4 inline-flex items-center gap-1 text-sm font-medium text-foreground hover:text-accent transition-colors"
      >
        {term.productLink.label}
        <ArrowUpRight className="w-4 h-4" />
      </Link>
    )}
  </div>
);

const LetterSection = ({ group, index }: { group: GlossaryLetterGroup; index: number }) => (
  <section
    id={`letter-${group.letter.toLowerCase()}`}
    className={`scroll-mt-36 ${index === 0 ? "" : "pt-14 md:pt-16"}`}
  >
    <header className="flex items-baseline gap-4 border-b border-border pb-4">
      <h2
        className="text-4xl md:text-5xl leading-none tracking-tight text-accent"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {group.letter}
      </h2>
      <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
        {group.terms.length} {group.terms.length === 1 ? "term" : "terms"}
      </span>
    </header>
    <div className="mt-6 divide-y divide-border/70">
      {group.terms.map((term) => (
        <TermRow key={term.id} term={term} />
      ))}
    </div>
  </section>
);

const FinalCta = () => (
  <section className="py-24 md:py-32 px-6 md:px-10 bg-muted/40">
    <div className="max-w-3xl mx-auto text-center flex flex-col items-center gap-8">
      <h2
        className="text-3xl md:text-5xl leading-[1.05] tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        See these metrics on your own site.
      </h2>
      <p className="text-muted-foreground max-w-xl">
        Bigdelta tracks visitors, sessions, funnels, and revenue in one place, so the terms above
        stop being vocabulary and start being numbers you can act on.
      </p>
      <div className="flex flex-col items-center gap-3">
        <a
          href={SIGN_UP_URL}
          onClick={trackSignUp}
          className="bg-foreground text-background px-6 py-3 rounded-full text-sm font-medium hover:bg-foreground/90 transition-colors"
        >
          Start free
        </a>
        <span className="text-sm text-muted-foreground">
          Connected in 2 minutes. No credit card required.
        </span>
      </div>
    </div>
  </section>
);

const Glossary = () => (
  <div className="min-h-screen bg-background text-foreground">
    <Navbar />
    <main>
      <Hero />
      <LetterBar />
      <div className="px-6 md:px-10 pt-12 md:pt-16 pb-16 md:pb-24">
        <article className="max-w-2xl mx-auto">
          {GLOSSARY_GROUPS.map((group, i) => (
            <LetterSection key={group.letter} group={group} index={i} />
          ))}
        </article>
      </div>
      <RelatedResources currentPath="/glossary" />
      <FinalCta />
    </main>
    <Footer />
  </div>
);

export default Glossary;
